// Stealth-mode helpers shared by landing + app.
//
// When APP_MODE.stealth = true:
//   - Landing hero CTA swapped for countdown + Follow @handle
//   - App's Overview shows "You're #N" card + share button
//   - Deposit actions disabled / replaced with launch date
//
// Single source of truth for the launch date + X handle is config.js.
// Flip APP_MODE.stealth = false to restore public mode — docs/fleet/STEALTH_MODE.md.

import { APP_MODE, VAULT_CONFIGS, VAULT_ABI, NETWORK } from './config.js';

const MS_PER_SEC = 1000;
const MS_PER_MIN = 60 * MS_PER_SEC;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

export function isStealth() {
  return !!APP_MODE.stealth;
}

// Depositor whitelist: wallets already holding shares (or with a pending
// withdrawal) see the full public app during stealth. Non-depositors see the
// stealth/countdown UI. New deposits stay locked in stealth for EVERYONE —
// this is an access gate for the info view, not a fee/deposit bypass.
//
// Cached per-address with a short TTL so page-to-page navigation doesn't
// re-hit the RPC. Cache is module-scoped; a hard refresh re-queries.
let _depositorCache = null;  // { addr, result, ts }
const _DEP_TTL_MS = 60 * 1000;

export async function isDepositor(address) {
  if (!address) return false;
  if (typeof ethers === 'undefined') return false;
  const lc = address.toLowerCase();
  const now = Date.now();
  if (_depositorCache && _depositorCache.addr === lc && (now - _depositorCache.ts) < _DEP_TTL_MS) {
    return _depositorCache.result;
  }
  try {
    const rp = new ethers.JsonRpcProvider(NETWORK.rpcUrl);
    const liveVaults = VAULT_CONFIGS.filter(v => v.status === 'live' && v.address);
    const reads = await Promise.all(liveVaults.map(async (vc) => {
      try {
        const vault = new ethers.Contract(vc.address, VAULT_ABI, rp);
        // A depositor mid-withdrawal (requested but not claimed) still counts.
        const [bal, wr] = await Promise.all([
          vault.balanceOf(address),
          vault.withdrawalRequests(address),
        ]);
        return bal > 0n || wr[0] > 0n;
      } catch {
        return false;
      }
    }));
    const result = reads.some(Boolean);
    _depositorCache = { addr: lc, result, ts: now };
    return result;
  } catch (e) {
    console.warn('[stealth] isDepositor check failed', e);
    return false;
  }
}

// Convenience: "should this page render its stealth view?" Returns true when
// stealth is on AND the connected wallet (if any) is not a depositor. Use at
// the top of each page's async init to decide stealth-vs-live rendering.
export async function shouldHideForStealth(address) {
  if (!isStealth()) return false;
  if (!address) return true;
  if (canBypassStealth(address)) return false;  // tester whitelist override
  return !(await isDepositor(address));
}

// Pre-launch tester whitelist (configured in config.js APP_MODE.deposit_whitelist).
// Whitelisted wallets bypass stealth entirely: they see the live UI AND can
// deposit (deposit-CTA lock is skipped for them). Existing depositors NOT on
// the list keep the current behavior — live UI but locked deposit CTA until
// launch_date. Returns false when stealth is off (no bypass needed) or when
// no whitelist is configured.
export function canBypassStealth(address) {
  if (!isStealth()) return false;
  if (!address) return false;
  const wl = APP_MODE.deposit_whitelist || [];
  const lc = String(address).toLowerCase();
  return wl.some((a) => String(a).toLowerCase() === lc);
}

export function launchDate() {
  return new Date(APP_MODE.launch_date);
}

export function launchDateLabel() {
  // "May 14, 2026"
  return launchDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function launchDateShort() {
  // "May 14"
  return launchDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function xHandle() {
  // Strip leading @ for URL building; keep it for display
  return (APP_MODE.x_handle || '').replace(/^@/, '');
}

export function xFollowUrl() {
  return `https://x.com/${xHandle()}`;
}

export function xShareUrl({ position, total } = {}) {
  const handle = APP_MODE.x_handle || '@CrystalClear';
  const lines = [];
  if (position != null) {
    lines.push(`I'm #${position} early for ${handle}.`);
  } else {
    lines.push(`Watching ${handle}.`);
  }
  lines.push(`Market-neutral systematic trading on @HyperliquidX. Out-of-sample tested, on-chain settled.`);
  lines.push(`Launching ${launchDateLabel()}.`);
  const text = encodeURIComponent(lines.join(' '));
  return `https://x.com/intent/post?text=${text}`;
}

/**
 * Renders a countdown into the target element. Call once; it auto-updates
 * every 30s. When the countdown hits zero, it shows "Launching now" —
 * manual flip of APP_MODE.stealth to false is still required to open deposits.
 */
export function startCountdown(el) {
  if (!el) return;
  const tgt = launchDate().getTime();
  const tick = () => {
    const ms = tgt - Date.now();
    if (ms <= 0) {
      el.innerHTML = `<span class="text-primary font-bold">Launching now</span>`;
      return;
    }
    const d = Math.floor(ms / MS_PER_DAY);
    const h = Math.floor((ms % MS_PER_DAY) / MS_PER_HOUR);
    const m = Math.floor((ms % MS_PER_HOUR) / MS_PER_MIN);
    el.innerHTML = `
      <div class="flex items-baseline gap-3 md:gap-5 font-headline">
        <div class="text-center">
          <div class="text-4xl md:text-6xl font-bold text-primary tabular-nums">${d}</div>
          <div class="text-[10px] md:text-xs uppercase tracking-widest text-on-surface-variant mt-1">days</div>
        </div>
        <div class="text-center">
          <div class="text-4xl md:text-6xl font-bold text-primary/90 tabular-nums">${String(h).padStart(2,'0')}</div>
          <div class="text-[10px] md:text-xs uppercase tracking-widest text-on-surface-variant mt-1">hours</div>
        </div>
        <div class="text-center">
          <div class="text-4xl md:text-6xl font-bold text-primary/70 tabular-nums">${String(m).padStart(2,'0')}</div>
          <div class="text-[10px] md:text-xs uppercase tracking-widest text-on-surface-variant mt-1">min</div>
        </div>
      </div>
    `;
  };
  tick();
  setInterval(tick, 30_000);
}

/**
 * Landing hero: swap the default "Deposit & Earn" CTAs for a countdown +
 * Follow-on-X CTA when stealth is on.
 */
export function applyStealthToLandingHero() {
  if (!isStealth()) return;
  const ctas = document.getElementById('hero-ctas');
  const stealth = document.getElementById('hero-stealth');
  if (!ctas || !stealth) return;

  ctas.classList.add('hidden');
  stealth.classList.remove('hidden');
  stealth.classList.add('flex');
  stealth.innerHTML = `
    <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface-container-low ghost-border">
      <span class="text-[10px] uppercase tracking-widest text-secondary-fixed-dim font-bold">Launching ${launchDateLabel()}</span>
    </div>
    <div id="hero-countdown" class="py-4"></div>
    <div class="flex flex-col sm:flex-row items-center gap-4">
      <a href="${xFollowUrl()}" target="_blank" rel="noopener"
         class="w-full sm:w-auto px-8 py-4 bg-gradient-to-br from-primary to-primary-container text-on-primary-container font-bold text-lg glow-hover transition-all duration-300 flex items-center justify-center gap-2">
        <span class="material-symbols-outlined">notifications_active</span>
        Follow ${APP_MODE.x_handle} for launch
      </a>
      <a href="app.html" class="w-full sm:w-auto px-8 py-4 glass-panel ghost-border text-on-surface font-semibold text-lg hover:bg-surface-variant/50 transition-all text-center">
        Explore the app
      </a>
    </div>
    <p class="text-xs text-on-surface-variant/60 max-w-md text-center mt-2">
      Trading live today on Onyx + Amber. Performance metrics unlock at launch.
      Connect your wallet in the app to reserve your spot.
    </p>
  `;
  startCountdown(document.getElementById('hero-countdown'));
}

/**
 * POST wallet address to /api/early-visitor (stealth mode only). Returns
 * {position, total, first_seen} or null on failure.
 * Dedups server-side by address.
 */
export async function registerEarlyVisitor(address) {
  if (!isStealth() || !address) return null;
  try {
    const r = await fetch('/api/early-visitor', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }),
    });
    if (!r.ok) return null;
    return await r.json();
  } catch (e) {
    console.warn('[stealth] early-visitor register failed', e);
    return null;
  }
}
